/**
 * crmSpec — capability-level wizard spec for CRM connectors.
 *
 * Vendors today: Salesforce, HubSpot. Both share the same flow:
 *   1. vendor_picker  → SF vs HubSpot (skipped if single-vendor narrowed)
 *   2. credentials    → optional BYOA (client_id + client_secret + sandbox)
 *   3. oauth          → redirect to vendor consent screen
 *
 * Per CLAUDE.md "ICP-fit tier": SF + HubSpot = Tier 1 priority connectors.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/SalesforcePage.tsx
 *   ando-frontend/app/pages/configs/salesforceConfig.tsx (SOTA pattern reused)
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Cloud, Database, Search, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import { vendorIcon } from "../components/vendorIcons";

export interface CrmConnection extends BaseConnection {
  readonly instance_url?: string;
  readonly salesforce_org_id?: string;
  readonly hubspot_portal_id?: string;
  readonly api_version?: string;
  readonly sandbox?: boolean;
  readonly scope?: string;
}

const iconCls = "h-4 w-4";

export const crmSpec: CapabilityWizardSpec<CrmConnection> = {
  capability: "crm",
  title: "CRM",
  subtitle: msg`Salesforce o HubSpot — pipeline, account, opportunità in chat`,
  icon: <Cloud className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "salesforce",
      label: "Salesforce",
      icon: vendorIcon("salesforce", iconCls),
      description: msg`Sandbox + Production, OAuth managed o BYOA`,
    },
    {
      id: "hubspot",
      label: "HubSpot",
      icon: vendorIcon("hubspot", iconCls),
      description: msg`Marketing + CRM hub`,
    },
  ],

  // ── Steps — May 24 2026 cleanup: ``vendor_picker`` removed ────────────
  // The vendor picker now lives in ``<AddAccountMenu>`` (UI-side) BEFORE
  // the connect dialog opens, so each spec ships only the post-pick
  // steps (credentials / oauth). ``spec.vendors`` array is still the
  // source of truth for which providers the picker shows.
  steps: [
    {
      id: "byoa",
      kind: "credentials",
      title: msg`Credenziali (opzionale)`,
      description: msg`Lascia vuoto per usare l'app gestita da Ando. Compila per BYOA.`,
      fields: [
        {
          name: "client_id",
          label: "Client ID",
          type: "string",
          placeholder: "3MVG9…",
        },
        {
          name: "client_secret",
          label: "Client Secret",
          type: "password",
        },
        {
          name: "sandbox",
          label: msg`Sandbox (solo Salesforce)`,
          type: "boolean",
        },
      ],
    },
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Verrai reindirizzato al provider per l'autorizzazione OAuth.`,
      oauth: { buttonLabel: msg`Autorizza con CRM` },
    },
  ],

  benefits: [
    {
      icon: <Search className={iconCls} aria-hidden />,
      title: msg`Ricerca naturale`,
      body: msg`Chiedi ad Ando di trovare account, deal e opportunità in linguaggio naturale.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Cross-referencing`,
      body: msg`Correla email, calendario e CRM in un'unica vista temporale.`,
    },
    {
      icon: <Database className={iconCls} aria-hidden />,
      title: msg`Report al volo`,
      body: msg`Pipeline summary, conversion rate, top accounts — pronti per la riunione.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Privato e sicuro`,
      body: msg`Token criptati at-rest, scope minimo, cancellazione GDPR garantita.`,
    },
  ],

  api: createCapabilityApi<CrmConnection>({ capability: "crm" }),

  connectionKeyField: "id" as keyof CrmConnection,
  getDisplayName: (c) => {
    if (c.instance_url) {
      try {
        return new URL(c.instance_url).hostname;
      } catch {
        // fallthrough
      }
    }
    return c.salesforce_org_id ?? c.hubspot_portal_id ?? c.id ?? i18nSingleton._(msg`Connessione`);
  },
  getSecondaryId: (c) => c.salesforce_org_id ?? c.hubspot_portal_id ?? null,
};
